<template>
    <div class="video-template-activity tw:flex tw:flex-col tw:gap-6">
        <div>
            <activity-generating-placeholder
                v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)"
                activity-type="video"
                :generation-status="generationStatus"
                :generation-error="generationError"
            />

            <template v-else-if="video">
                <video-player :video-id="video.id" />

                <div class="m-t-md">
                    <h4 class="title is-4">{{ video.title }}</h4>

                    <p v-if="video.caption" class="has-text-subtle">
                        {{ video.caption }}
                    </p>
                </div>
            </template>
        </div>

        <e-field>
            <display-content :content="activityContent.content" />
        </e-field>

        <div
            v-if="activity.activity_settings.acknowledgment && !readOnly"
            class="video-template-activity__acknowledgment"
        >
            <e-field label="Acknowledgment">
                <e-checkbox v-if="!completed" v-model="userAccepted">
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>

                <!-- Bare model-value is not boolean true for PrimeVue; keep the bound form. -->
                <!-- eslint-disable-next-line vue/prefer-true-attribute-shorthand -->
                <e-checkbox v-else disabled :model-value="true">
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>
            </e-field>
        </div>

        <activity-completion-action-row>
            <e-button
                v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                :loading="isLoading"
                :disabled="disable"
                type="is-primary"
                @click="submit"
                @keyup="submit"
            >
                {{ translate('mark_complete') }}
            </e-button>
        </activity-completion-action-row>

        <submissions
            v-if="!readOnly && canViewSubmissions"
            :namespace="namespace"
            @fetch-submissions="toggleSubmissionLoading"
        />
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import ActivityGeneratingPlaceholder from '@/components/activity-types/course-activity-types/activities/shared/ActivityGeneratingPlaceholder.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import VideoPlayer from '@/components/videos/VideoPlayer.vue';
import { useActivity } from '@/composables/useActivity.js';
import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import axios from '@/plugins/axios.js';
import { loadRendererActivityContent } from '@/utilities/rendererActivityContent.js';

const PENDING_GENERATION_STATUSES = ['pending', 'queued', 'processing', 'generating'];
const POLL_INTERVAL_MS = 15000;

export default {
    name: 'VideoTemplate',

    components: {
        ActivityCompletionActionRow,
        ActivityGeneratingPlaceholder,
        DisplayContent,
        Submissions,
        VideoPlayer,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    data: () => ({
        video: null,
        userAccepted: false,
        generationStatus: null,
        generationError: null,
        statusPollIntervalId: null,
    }),

    computed: {
        isGenerationStatusUiEnabled: () => ACTIVITY_GENERATION_STATUS_UI_ENABLED,

        isGenerating() {
            if (PENDING_GENERATION_STATUSES.includes(this.generationStatus)) return true;
            return (
                !!this.generationStatus &&
                this.generationStatus !== 'failed' &&
                !this.video &&
                !this.activityContent?.video_id
            );
        },

        isFailedGeneration() {
            return this.generationStatus === 'failed';
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: { acknowledgment: this.userAccepted },
            };
        },

        disable() {
            if (this.activity.activity_settings.acknowledgment) {
                return this.disabled || !this.userAccepted;
            }

            return this.disabled;
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },
    },

    async created() {
        await this.loadVideo();
    },

    beforeUnmount() {
        this.clearStatusPolling();
    },

    methods: {
        async loadVideo({ refresh = false } = {}) {
            const content = await loadRendererActivityContent({
                namespace: this.namespace,
                activityContent: this.activityContent,
                key: 'video',
                refresh,
                fetch: async () => {
                    const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
                    const endpoint = this.namespace === 'Activities' ? 'activities' : 'activity-templates';
                    const { data } = await axios.get(`/api/${endpoint}/${id}?include=contentable.video`);

                    return data.data?.activity_content;
                },
            });

            this.generationStatus = content?.generation_status ?? null;
            this.generationError = content?.generation_error ?? null;

            // The video reference is linked at generation submit-time, but the playable
            // transcode is only ready once MediaConvert completes. Fetching the video here
            // (when a reference_id exists) also triggers the show-endpoint MediaConvert poll,
            // which self-heals a missed completion webhook. Guard against a null video.
            const referenceId = content?.video?.reference_id;

            if (referenceId) {
                this.video = await this.$store.dispatch('Videos/fetchOne', referenceId);
            }

            this.ensureStatusPolling();
        },

        ensureStatusPolling() {
            if (!this.isGenerating) {
                this.clearStatusPolling();
                return;
            }
            if (this.statusPollIntervalId) return;

            this.statusPollIntervalId = setInterval(() => {
                this.loadVideo({ refresh: true }).catch(() => {});
            }, POLL_INTERVAL_MS);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';

.video-template-activity {
    > * {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-completion-action-row) {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-submissions-section) {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
    }
}

// Desktop/tablet (>= 769px) must render exactly as before the mobile spacing work
// (868kj17g6 AC-6, ADR-051 decision 5). The previous markup stacked Bulma `.columns`
// (1.5rem rhythm, $column-gap padding), so only the spots where that rhythm differs from
// the flat 1.5rem gap are corrected here; mobile is unchanged.
@media screen and (min-width: 769px) {
    // Last `.column` padding plus the row's own `tw:mt-4` (it out-collapses the
    // `.columns` bottom margin): $column-gap + 1rem above the action row.
    .video-template-activity > .activity-completion-action-row {
        margin-top: calc(#{$column-gap} + 1rem - 1.5rem) !important;
    }

    // After the acknowledgment block its `m-b-md` (1rem) collapsed with the row's `tw:mt-4`.
    .video-template-activity > .video-template-activity__acknowledgment + .activity-completion-action-row {
        margin-top: calc(1rem - 1.5rem) !important;
    }

    // The submissions `.columns` top margin cancelled its `.column` padding, so the list
    // started right under the action row.
    .video-template-activity > .activity-completion-action-row + .activity-submissions-section {
        margin-top: -1.5rem !important;
    }
}
</style>
